import React from 'react';
import 'katex/dist/katex.min.css';
// @ts-ignore - react-katex types
import { InlineMath, BlockMath } from 'react-katex';
// @ts-ignore - react-syntax-highlighter types
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
// @ts-ignore - react-syntax-highlighter styles
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { DisplayItem } from '../../types/lesson';

interface ContentRendererProps {
  item: DisplayItem | string; // Support both DisplayItem objects and plain strings
  className?: string;
}



// Inline TextDescription component for graph/plot descriptions
const TextDescription: React.FC<{
  item: {
    content: string;
    content_type: string;
    metadata?: {
      tool_suggestion?: string;
    };
    description?: string;
  };
  className?: string;
}> = ({ item, className = '' }) => {
  const getToolUrl = (tool: string) => {
    switch (tool) {
      case 'desmos':
        return 'https://www.desmos.com/calculator';
      case 'geogebra':
        return 'https://www.geogebra.org/graphing';
      case 'wolfram':
        return 'https://www.wolframalpha.com/';
      default:
        return null;
    }
  };

  const toolUrl = item.metadata?.tool_suggestion ?
    getToolUrl(item.metadata.tool_suggestion) : null;

  return (
    <div className={`text-description ${className}`}>
      <div className="description-header">
        <h4>📋 Graph Description</h4>
        <span className="description-note">Visual content described in text</span>
      </div>

      <div className="description-content">
        <pre>{item.content}</pre>
      </div>

      <div className="description-actions">
        {toolUrl && (
          <a
            href={toolUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="open-tool-btn"
          >
            Open in {item.metadata?.tool_suggestion || 'External Tool'} ↗
          </a>
        )}
      </div>

      <div className="description-help">
        <p>Use the suggested tool above to visualize this graph or plot.</p>
      </div>
    </div>
  );
};

// Inline ChemicalFormula component
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ChemicalFormula: React.FC<{ content: string; className?: string }> = ({ content, className = '' }) => {
  // Convert simple chemical notation to HTML
  const formatChemical = (formula: string) => {
    return formula
      .replace(/(\d+)/g, '<sub>$1</sub>')  // Numbers as subscripts
      .replace(/\^(\d+)/g, '<sup>$1</sup>') // ^numbers as superscripts
      .replace(/→/g, ' → ')                 // Reaction arrows
      .replace(/\+/g, ' + ');               // Plus signs with spacing
  };

  return (
    <div
      className={`chemical-formula ${className}`}
      dangerouslySetInnerHTML={{ __html: formatChemical(content) }}
      style={{
        fontFamily: 'monospace',
        fontSize: '1.1em',
        textAlign: 'center',
        margin: '0.5rem 0'
      }}
    />
  );
};

const ContentRenderer: React.FC<ContentRendererProps> = ({ item, className = '' }) => {
  console.log('Rendering content item:', item);

  // Handle plain string items first
  if (typeof item === 'string') {
    return (
      <div className="content-item plaintext">
        <div className="formatted-text">
          {item.split('\n').map((line, index) => (
            <div key={index}>{line}</div>
          ))}
        </div>
      </div>
    );
  }

  // Now TypeScript knows item is DisplayItem
  const displayItem: DisplayItem = item;

  const renderContent = () => {
    switch (displayItem.render_method) {
      case 'katex':
        // Render LaTeX math - with error handling for complex content
        try {
          return (
            <div className="math-content" style={{ overflowX: 'auto', maxWidth: '100%' }}>
              {displayItem.content.includes('\\\\') || displayItem.content.includes('\\begin') ?
                <BlockMath math={displayItem.content} /> :
                <InlineMath math={displayItem.content} />
              }
            </div>
          );
        } catch (error) {
          console.warn('KaTeX rendering failed, falling back to plaintext:', error);
          return (
            <div className="katex-fallback">
              <p className="error-notice">⚠️ Could not render equation</p>
              <pre className="latex-code">{displayItem.content}</pre>
              <p className="help-text">This content may require external rendering tools.</p>
            </div>
          );
        }

      case 'highlight':
        // Render code with syntax highlighting
        return (
          <SyntaxHighlighter
            language={displayItem.metadata?.language || 'text'}
            style={tomorrow}
            showLineNumbers={displayItem.content.split('\n').length > 3}
          >
            {displayItem.content}
          </SyntaxHighlighter>
        );

      case 'description':
        // Render text descriptions (for graphs/plots that can't be displayed)
        return <TextDescription item={displayItem} />;

      case 'plaintext':
      default:
        // Render formatted plain text
        return (
          <div className="formatted-text">
            {displayItem.content.split('\n').map((line, index) => (
              <div key={index}>{line}</div>
            ))}
          </div>
        );
    }
  };

  return (
    <div
      className={`content-item ${displayItem.content_type || 'plaintext'} ${className}`}
      data-render-method={displayItem.render_method || 'plaintext'}
      title={displayItem.description}
      style={{ maxWidth: '100%', overflowX: 'auto' }}
    >
      {renderContent()}
      {displayItem.description && (
        <div className="content-description sr-only">
          {displayItem.description}
        </div>
      )}
    </div>
  );
};

export default ContentRenderer;
